import React, { useMemo, useState } from 'react';
import { Search, SlidersHorizontal, ChevronDown, ChevronRight, Trash2, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { CATEGORIES, CATEGORY_LABEL, categoryOf, groupByPhase, sum } from './materialUtils';
import { fmt } from '../../../lib/projectVisuals';

const FilterMenu = ({ filters, setFilters }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="op-btn-ghost h-10 text-[13px]" data-testid="material-filter-btn"><SlidersHorizontal size={15} />Filter</button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-64 rounded-2xl space-y-3">
      <label className="flex items-center justify-between text-sm">Visa firma-utrustning
        <Switch checked={filters.firm} onCheckedChange={(v) => setFilters((f) => ({ ...f, firm: v }))} data-testid="filter-firm-switch" /></label>
      <label className="flex items-center justify-between text-sm">Bara låg säkerhet
        <Switch checked={filters.lowConf} onCheckedChange={(v) => setFilters((f) => ({ ...f, lowConf: v }))} data-testid="filter-lowconf-switch" /></label>
      <div className="text-sm">
        <p className="mb-1.5 text-stone-500 text-xs">Sortera inom byggdel</p>
        <div className="flex gap-1.5">
          {[['default', 'Standard'], ['cost', 'Kostnad'], ['name', 'Namn']].map(([k, l]) => (
            <button key={k} className="op-chip" data-active={filters.sort === k} onClick={() => setFilters((f) => ({ ...f, sort: k }))} data-testid={`filter-sort-${k}`}>{l}</button>
          ))}
        </div>
      </div>
    </PopoverContent>
  </Popover>
);

const EditRow = ({ m, onField, onRemove }) => (
  <tr className="border-t border-stone-100" data-testid={`edit-row-${m._idx}`}>
    <td className="py-1.5 pr-2" colSpan={2}><Input className="h-8 text-sm" value={m.specific_product || m.name || ''} onChange={(e) => onField(m._idx, 'name', e.target.value)} data-testid={`edit-name-${m._idx}`} /></td>
    <td className="py-1.5 pr-2"><Input className="h-8 text-sm text-right" type="number" value={m.quantity ?? ''} onChange={(e) => onField(m._idx, 'quantity', e.target.value)} data-testid={`edit-qty-${m._idx}`} /></td>
    <td className="py-1.5 pr-2"><Input className="h-8 text-sm w-16" value={m.unit || ''} onChange={(e) => onField(m._idx, 'unit', e.target.value)} data-testid={`edit-unit-${m._idx}`} /></td>
    <td className="py-1.5 pr-2"><Input className="h-8 text-sm text-right" type="number" value={m.estimated_unit_price ?? ''} onChange={(e) => onField(m._idx, 'estimated_unit_price', e.target.value)} data-testid={`edit-price-${m._idx}`} /></td>
    <td className="py-1.5 pr-2 text-right text-sm font-semibold op-num">{fmt(m.estimated_total_price)}</td>
    <td className="py-1.5 text-right"><button onClick={() => onRemove(m._idx)} className="p-1.5 text-stone-400 hover:text-red-600" data-testid={`remove-row-${m._idx}`}><Trash2 size={14} /></button></td>
  </tr>
);

const PhaseGroup = ({ g, area, open, toggle, onRowClick, onPhaseClick, editing, onField, onRemove, active }) => {
  const total = sum(g.rows);
  return (
    <tbody data-testid={`phase-group-${g.name}`}>
      {g.rows.map((m, i) => (editing ? <EditRow key={m._idx} m={m} onField={onField} onRemove={onRemove} /> : (
        (open || i === 0) && (
          <tr key={m._idx} className={`border-t border-stone-100 hover:bg-[#F26B1F]/[0.04] cursor-pointer transition-colors ${active ? 'bg-[#F26B1F]/[0.03]' : ''}`}
            onClick={() => (i === 0 && !open ? toggle() : onRowClick(m))} data-testid={`material-row-${m._idx}`}>
            <td className="py-2.5 pr-3 align-top w-[170px]">
              {i === 0 && (
                <button onClick={(e) => { e.stopPropagation(); toggle(); onPhaseClick(g.name); }} className="flex items-center gap-1.5 font-semibold text-stone-900 text-left" data-testid={`phase-toggle-${g.name}`}>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}<span className="line-clamp-2">{g.name}</span>
                </button>
              )}
            </td>
            <td className="py-2.5 pr-3 text-stone-800">{!open && i === 0 ? <span className="text-stone-400">{g.rows.length} rader</span> : (m.specific_product || m.name)}</td>
            <td className="py-2.5 pr-3 text-right op-num">{open ? fmt(m.quantity, 1) : ''}</td>
            <td className="py-2.5 pr-3 text-stone-500">{open ? m.unit : ''}</td>
            <td className="py-2.5 pr-3 text-right op-num">{open ? fmt(m.estimated_unit_price, 1) : ''}</td>
            <td className="py-2.5 pr-3 text-right op-num font-medium">{open ? fmt(m.estimated_total_price) : fmt(total)}</td>
            <td className="py-2.5 text-right op-num text-stone-500">{area ? `${fmt((open ? m.estimated_total_price : total) / area)} kr/m²` : '—'}</td>
          </tr>
        )
      )))}
      {open && !editing && (
        <tr className="border-t border-stone-200 bg-stone-50/80">
          <td /><td className="py-2 font-semibold text-stone-900 text-[12.5px]">Summa {g.name.toLowerCase()}</td><td colSpan={3} />
          <td className="py-2 pr-3 text-right font-bold op-num">{fmt(total)}</td>
          <td className="py-2 text-right font-semibold op-num text-stone-600">{area ? `${fmt(total / area)} kr/m²` : '—'}</td>
        </tr>
      )}
    </tbody>
  );
};

const MobileList = ({ groups, onRowClick }) => (
  <div className="md:hidden divide-y divide-stone-100" data-testid="material-mobile-list">
    {groups.flatMap((g) => g.rows).map((m) => (
      <button key={m._idx} onClick={() => onRowClick(m)} className="w-full flex items-center gap-3 py-3 text-left" data-testid={`material-mobile-row-${m._idx}`}>
        <span className="w-11 h-11 rounded-xl bg-stone-100 text-[10px] font-bold text-stone-500 flex items-center justify-center flex-shrink-0">{CATEGORY_LABEL[categoryOf(m)].slice(0, 3).toUpperCase()}</span>
        <span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{m.specific_product || m.name}</span>
          <span className="block text-xs text-stone-500 op-num">{fmt(m.quantity, 1)} {m.unit}</span></span>
        <span className="text-sm font-semibold op-num">{fmt(m.estimated_total_price)} kr</span><ChevronRight size={15} className="text-stone-400" />
      </button>
    ))}
  </div>
);

export const MaterialTable = ({ materials, area, editing, onField, onAddRow, onRemove, onRowClick, onPhaseClick, activePhase }) => {
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ firm: true, lowConf: false, sort: 'default' });
  const [closed, setClosed] = useState({});

  const present = useMemo(() => new Set(materials.map(categoryOf)), [materials]);
  const groups = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const rows = materials.map((m, idx) => ({ ...m, _idx: idx })).filter((m) => editing || (
      (cat === 'all' || categoryOf(m) === cat)
      && (filters.firm || m.category !== 'Firma-utrustning')
      && (!filters.lowConf || m.confidence === 'low')
      && (!ql || `${m.specific_product || m.name} ${m.phase} ${m.where_to_buy || ''}`.toLowerCase().includes(ql))));
    const gs = groupByPhase(rows);
    gs.forEach((g) => {
      if (filters.sort === 'cost') g.rows.sort((a, b) => (b.estimated_total_price || 0) - (a.estimated_total_price || 0));
      if (filters.sort === 'name') g.rows.sort((a, b) => String(a.specific_product || a.name).localeCompare(String(b.specific_product || b.name), 'sv'));
    });
    return gs;
  }, [materials, cat, q, filters, editing]);
  const filteredTotal = groups.reduce((s, g) => s + sum(g.rows), 0);

  return (
    <div data-testid="material-table">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="flex gap-1.5 overflow-x-auto op-noscroll flex-1" data-testid="material-category-chips">
          <button className="op-chip" data-active={cat === 'all'} onClick={() => setCat('all')} data-testid="chip-all">Alla</button>
          {[...CATEGORIES, { key: 'ovrigt', label: 'Övrigt' }].filter((c) => present.has(c.key)).map((c) => (
            <button key={c.key} className="op-chip" data-active={cat === c.key} onClick={() => setCat(c.key)} data-testid={`chip-${c.key}`}>{c.label}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1 lg:w-60">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sök i tabellen…" data-testid="material-search-input"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#F26B1F]" />
          </div>
          <FilterMenu filters={filters} setFilters={setFilters} />
        </div>
      </div>
      <div className="hidden md:block overflow-x-auto rounded-xl border border-stone-200">
        <table className="w-full text-[12.5px]">
          <thead className="bg-stone-50 text-[11px] text-stone-500">
            <tr>
              {['Byggdel', 'Material', 'Mängd', 'Enhet', 'Á-pris (kr)', 'Total kostnad (kr)', 'Kostnad per m²'].map((h, i) => (
                <th key={h} className={`py-2.5 font-semibold ${i >= 2 && i !== 3 ? 'text-right pr-3' : 'text-left pr-3'} ${i === 0 ? 'pl-3' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          {groups.map((g) => (
            <PhaseGroup key={g.name} g={g} area={area} open={editing || !closed[g.name]} toggle={() => setClosed((c) => ({ ...c, [g.name]: !c[g.name] }))}
              onRowClick={onRowClick} onPhaseClick={onPhaseClick} editing={editing} onField={onField} onRemove={onRemove} active={activePhase === g.name} />
          ))}
          <tfoot>
            <tr className="border-t-2 border-stone-300 bg-white">
              <td className="py-3 pl-3 font-bold" colSpan={2}>Totalt <span className="font-normal text-stone-500">(enligt valda byggdelar)</span></td><td colSpan={3} />
              <td className="py-3 pr-3 text-right font-bold op-num" data-testid="material-table-total">{fmt(filteredTotal)}</td>
              <td className="py-3 pr-3 text-right font-bold op-num">{area ? `${fmt(filteredTotal / area)} kr/m²` : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {!editing && <MobileList groups={groups} onRowClick={onRowClick} />}
      {editing && <button onClick={onAddRow} className="op-btn-ghost mt-3 h-9 text-[13px]" data-testid="add-material-btn"><Plus size={14} />Lägg till rad</button>}
      {groups.length === 0 && <p className="py-8 text-center text-sm text-stone-400" data-testid="material-table-empty">Inga rader matchar filtret.</p>}
    </div>
  );
};
